/**
 * useFilings.ts
 *
 * The filing action sequence: precheck (duplicate warning) -> create
 * filing -> recent list + per-target undo. Precheck/create/undo are all
 * user-triggered POSTs, modeled as mutations rather than queries; a
 * successful create or undo invalidates the recent-filings list.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api } from "../api/client";
import type {
  ApiError,
  FilingGroupResult,
  FilingPrecheckRequest,
  FilingPrecheckResponse,
  FilingRequest,
  RecentFilingsResponse,
  UndoFilingTargetResponse,
} from "../api/types";

const RECENT_FILINGS_KEY = ["filings", "recent"] as const;

export function usePrecheckFiling(): UseMutationResult<
  FilingPrecheckResponse,
  ApiError,
  FilingPrecheckRequest
> {
  return useMutation<FilingPrecheckResponse, ApiError, FilingPrecheckRequest>({
    mutationFn: (req) => api.precheckFiling(req),
  });
}

export function useCreateFiling(): UseMutationResult<
  FilingGroupResult,
  ApiError,
  FilingRequest
> {
  const queryClient = useQueryClient();
  return useMutation<FilingGroupResult, ApiError, FilingRequest>({
    mutationFn: (req) => api.createFiling(req),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: RECENT_FILINGS_KEY });
    },
  });
}

export function useRecentFilings(): UseQueryResult<
  RecentFilingsResponse,
  ApiError
> {
  return useQuery<RecentFilingsResponse, ApiError>({
    queryKey: RECENT_FILINGS_KEY,
    queryFn: api.getRecentFilings,
  });
}

export function useUndoFilingTarget(): UseMutationResult<
  UndoFilingTargetResponse,
  ApiError,
  number
> {
  const queryClient = useQueryClient();
  return useMutation<UndoFilingTargetResponse, ApiError, number>({
    mutationFn: (targetId) => api.undoFilingTarget(targetId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: RECENT_FILINGS_KEY });
    },
  });
}
